import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import './PhotoManager.css';
import { useToast } from '../UI/ToastContext';

const PhotoManager = () => {
  const [photos, setPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [imageCache, setImageCache] = useState({});
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const { showInfo } = useToast();
  
  // API base URL from environment variables
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

  // Fetch user's photos on component mount
  useEffect(() => {
    fetchUserPhotos();
  }, []);

  // Fetch all user photos from the API
  const fetchUserPhotos = async () => {
    try {
      debugLog('Fetching user photos from API');
      setError(null);
      setLoadingImages({});
      setImageCache({});
      const response = await api.get('/photos');
      
      debugLog('API response received:', response.data);
      
      if (response.data.success) {
        debugLog(`Received ${response.data.files?.length || 0} photos from API`);
        
        // Process photos
        const processedPhotos = response.data.files.map(photo => {
          debugLog(`Processing photo: ${photo.filename}`, photo);
          // Set loading state for this image
          setLoadingImages(prev => ({
            ...prev,
            [photo.filename]: true
          }));
          
          return photo;
        });
        
        setPhotos(processedPhotos || []);
        
        // Load image data for each photo
        processedPhotos.forEach(photo => {
          loadImageData(photo.filename);
        });
      } else {
        debugLog('API request successful but returned no success flag');
      }
    } catch (err) {
      debugLog('Error fetching photos:', err);
      setError('Failed to load your photos. Please try again later.');
    }
  };

  // Load image data from the API
  const loadImageData = async (filename) => {
    try {
      debugLog(`Loading image data for ${filename}`);
      const response = await api.get(`/photos/data/${filename}`);
      
      if (response.data.success && response.data.data) {
        const imageUrl = `data:${response.data.content_type};base64,${response.data.data}`;
        
        // Store image data in cache
        setImageCache(prev => ({
          ...prev,
          [filename]: imageUrl
        }));
        
        // Mark loading as finished
        setLoadingImages(prev => ({
          ...prev,
          [filename]: false
        }));
        
        // Clear any previous errors
        if (imageErrors[filename]) {
          setImageErrors(prev => {
            const newErrors = {...prev};
            delete newErrors[filename];
            return newErrors;
          });
        }
      }
    } catch (err) {
      debugLog(`Error loading image data for ${filename}:`, err);
      handleImageError(filename, {
        type: 'error',
        message: err.response?.data?.error || 'Failed to load image data'
      });
      
      // Mark loading as finished
      setLoadingImages(prev => ({
        ...prev,
        [filename]: false
      }));
    }
  };

  // Function for improved logging
  const debugLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    const logPrefix = `[PhotoManager ${timestamp}]`;
    
    if (data) {
      console.log(`${logPrefix} ${message}`, data);
    } else {
      console.log(`${logPrefix} ${message}`);
    }
  };

  // Handle image load error
  const handleImageError = (filename, e) => {
    debugLog(`Error for image ${filename}:`, e);
    
    // Track which images failed to load with precise message
    let errorMessage = 'Failed to load image';
    if (e.message) {
      errorMessage = e.message;
    } else if (e.type === 'error') {
      errorMessage = 'Error loading image data';
    }
    
    setImageErrors(prev => ({
      ...prev,
      [filename]: errorMessage
    }));
  };

  // Handle file selection through the file input
  const handleFileSelect = (event) => {
    uploadFiles(event.target.files);
  };

  // Handle file drop
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      uploadFiles(event.dataTransfer.files);
    }
  };

  // Handle drag events
  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Upload files to the server
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      
      // Add each selected file to the FormData
      for (let i = 0; i < files.length; i++) {
        formData.append('photos', files[i]);
      }

      // Upload the files with progress tracking
      const response = await api.post('/photos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      // Handle success
      if (response.data.success) {
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Clear previous image errors
        setImageErrors({});
        
        // Show message about rejected files if any
        if (response.data.rejected && response.data.rejected.length > 0) {
          setError(`Uploaded ${response.data.files.length} file(s), but ${response.data.rejectedCount} file(s) were rejected because they weren't valid images.`);
        }
        
        // Refetch the photos to update the list
        fetchUserPhotos();
      }
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError(err.response?.data?.error || 'Failed to upload photos. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Delete a photo
  const deletePhoto = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    try {
      setError(null);
      const response = await api.delete(`/photos/${filename}`);
      
      if (response.data.success) {
        // Update the photos list by removing the deleted photo
        setPhotos(photos.filter(photo => photo.filename !== filename));
        
        // Clear any error for this image
        if (imageErrors[filename]) {
          const newErrors = { ...imageErrors };
          delete newErrors[filename];
          setImageErrors(newErrors);
        }
        
        // Remove from cache
        if (imageCache[filename]) {
          const newCache = { ...imageCache };
          delete newCache[filename];
          setImageCache(newCache);
        }
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete the photo. Please try again.');
    }
  };

  // Trigger file input click
  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Refresh photos
  const refreshPhotos = () => {
    setPhotos([]); // Clear current photos
    setImageErrors({});
    setImageCache({});
    fetchUserPhotos();
  };

  return (
    <div className="photo-manager">
      <div className="photo-manager__header">
        <h1>Photo Manager</h1>
        <p>Upload and manage your reference photos for avatar creation</p>
      </div>

      {/* Upload Section */}
      <div 
        className={`photo-manager__upload-area ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="photo-manager__file-input"
        />
        
        <div className="photo-manager__upload-content">
          <div className="photo-manager__upload-icon">
            <i className="fas fa-cloud-upload-alt"></i>
          </div>
          <h3>Drag and drop your photos here</h3>
          <p>or</p>
          <button 
            className="btn btn-primary"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering parent onClick
              openFileDialog();
            }}
            disabled={isUploading}
          >
            Browse Files
          </button>
          <p className="photo-manager__file-types">Supported formats: JPG, PNG, WEBP</p>
        </div>
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div className="photo-manager__progress">
          <div className="photo-manager__progress-bar">
            <div 
              className="photo-manager__progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p>{uploadProgress}% Uploaded</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="photo-manager__error">
          {error}
        </div>
      )}

      {/* Photos Gallery */}
      <div className="photo-manager__gallery">
        <div className="photo-manager__gallery-header">
          <h2>Your Photos</h2>
          <div className="photo-manager__actions">
            <button 
              className="photo-manager__refresh-btn"
              onClick={refreshPhotos}
              aria-label="Refresh photos"
            >
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
            <button
              className="photo-manager__debug-btn"
              onClick={() => {
                debugLog('--- DEBUG INFO ---');
                debugLog(`API Base URL: ${apiBaseUrl}`);
                debugLog(`Token exists: ${Boolean(localStorage.getItem('access_token') || localStorage.getItem('token'))}`);
                debugLog('Image Cache Size:', Object.keys(imageCache).length);
                debugLog('Photo paths:');
                photos.forEach(photo => {
                  debugLog(`Photo ${photo.filename}:`, {
                    originalPath: photo.path,
                    url: photo.url,
                    inCache: Boolean(imageCache[photo.filename]),
                    error: imageErrors[photo.filename] || 'none'
                  });
                });
                showInfo('Debug information logged to console. Open browser developer tools to view.');
              }}
              aria-label="Debug info"
            >
              <i className="fas fa-bug"></i> Debug Info
            </button>
          </div>
        </div>
        {photos.length === 0 ? (
          <p className="photo-manager__empty">You haven't uploaded any photos yet.</p>
        ) : (
          <div className="photo-manager__grid">
            {photos.map((photo) => (
              <div key={photo.filename} className="photo-manager__item">
                <div className="photo-manager__image-container">
                  {loadingImages[photo.filename] && (
                    <div className="photo-manager__loading">
                      <div className="photo-manager__spinner"></div>
                    </div>
                  )}
                  <img 
                    src={imageCache[photo.filename] || '/placeholder-image.svg'}
                    alt={photo.filename}
                    className={`photo-manager__image ${imageErrors[photo.filename] ? 'photo-manager__image-error' : ''}`}
                    style={{opacity: loadingImages[photo.filename] ? 0 : 1}}
                    loading="lazy"
                    title={photo.filename}
                  />
                  {imageErrors[photo.filename] && (
                    <div className="photo-manager__error-tooltip">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>{imageErrors[photo.filename]}</span>
                    </div>
                  )}
                  <div className="photo-manager__overlay">
                    <button 
                      className="photo-manager__delete-btn"
                      onClick={() => deletePhoto(photo.filename)}
                      aria-label={`Delete ${photo.filename}`}
                      disabled={loadingImages[photo.filename]}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div className="photo-manager__details">
                  <p className="photo-manager__filename" title={photo.filename}>
                    {photo.filename.length > 25 
                      ? photo.filename.substring(0, 22) + '...' 
                      : photo.filename
                    }
                  </p>
                  <p className="photo-manager__filesize">
                    {(photo.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoManager; 